// Suporte para a lib buffer
(window as any).global = window;

import { throwError as observableThrowError } from 'rxjs';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { catchError, tap } from 'rxjs/operators';
import { TORO_USER_API_URLS } from '../../constants/user/api-routes.constants';
import { MetaTagType } from '../../enum/seo/meta-tag-type.enum';
import { AppState } from '../../interfaces/app-state.interface';
import { ignorePreviousUserParamName } from '../../resources/consts';
import { InitializerAction } from '../../state-management/initializer/initiliazer.action';
import { StorageService } from '../storage.service';
import { RoutesTable } from './../../resources/routes-table.model';
import { Buffer } from 'buffer';

const environmentFilePath =
  RoutesTable.environment.routes.getEnvironmentConfig.uri;
const defaultTitle =
  'Toro Investimentos - A melhor experiência digital de investimentos';
const defaultMetaTags = [
  {
    name: MetaTagType.Description,
    content:
      'A melhor experiência digital de investimentos do mundo - Crie o seu Futuro'
  }
];

@Injectable()
export class SettingsService {
  private settings: any = {};
  private defaultEnvironment: string;
  private environment: string;
  private defaultTitle = defaultTitle;
  private defaultMetaTags = defaultMetaTags;

  constructor(
    private httpClient: HttpClient,
    private store$: Store<AppState>,
    private metaService: Meta,
    private titleService: Title
  ) {}

  /**
   * Define a url absoluta com base em uma uri e a url base da api
   * @param uri uri do recurso
   */
  public baseUrl(uri: string): string {
    if (this.shouldUseNewuserApi(uri)) {
      return `${this.settings.backend.user}${uri}`;
    }

    return `${this.settings.ub}${uri}`;
  }

  public getConfigurationKeyValue(key: string): any {
    return this.settings ? this.settings[key] : null;
  }

  /**
   * Carrega as configuracões do ambiente (arquivo environment.json)
   */
  public loadEnvironmentSettings(): Promise<any> {
    const ignoreUser =
      `${window.location.search}`.match(ignorePreviousUserParamName) != null;
    const headers = new HttpHeaders().set(
      'Content-Type',
      'text/plain; charset=utf-8'
    );

    return new Promise(resolve => {
      this.httpClient
        .get(`${environmentFilePath}?scape=${Math.random()}`, {
          headers: headers,
          responseType: 'text'
        })
        .pipe(
          tap(response => {
            var envJson = this.decodeEnv(response);
            this.successGetEnv(envJson.settings, envJson.env, ignoreUser);
            resolve(true);
          }),
          catchError((error: any): any => {
            resolve(true);
            error = JSON.stringify(error);

            const message =
              'Não foi possível carregar o arquivo de ambiente "environment.json". Detalhes do erro: ' +
              error.message;
            return observableThrowError(message);
          })
        )
        .subscribe();
    });
  }

  private decodeEnv(data: string) {
    const isBase64 = /^[-A-Za-z0-9+\/]*={0,3}$/.test(data);
    const json = isBase64
      ? Buffer.from(data, 'base64').toString('utf-8')
      : data;

    return JSON.parse(json);
  }

  private successGetEnv(settings, env, ignoreUser: boolean) {
    this.settings = settings;
    this.environment = env;
    this.defaultEnvironment = this.getConfigurationKeyValue('ub');
    StorageService.applicationDomain = settings.cad;
    this.store$.dispatch({
      type: InitializerAction.settingsLoaded,
      payload: {
        settings: settings,
        ignorePreviousUser: ignoreUser
      }
    });
  }

  /**
   * Define a versão atual do app
   * @param version versão
   */
  public setAppVersion(version: string) {
    this.settings.version = version;
  }

  /**
   * Define a versão atual do pacote common
   * @param version versão
   */
  public setCommonPackageVersion(version: string) {
    this.settings.commonPackageVersion = version;
  }

  /** Use default environment */
  public resetEnvironmentDomainToDefault() {
    this.swtichEnvironmentDomain(this.defaultEnvironment);
  }

  /** Get the default environment (api url) */
  public getDefaultEnvironment(): string {
    return this.defaultEnvironment;
  }

  /** Get the environment (env property) */
  public getEnvironment(): string {
    return this.environment;
  }

  /** Recupera um snapshot da configuração atual */
  public getSettingsSnapshot() {
    return this.settings;
  }

  /**
   * ATENÇÃO: Função temporária, apenas para fins de separação do Site e App logado
   * Define se a aplicação está em um dominio em que deve se comportar como logada.
   */
  public isOnLoggedDomain() {
    const { origin } = window.location;

    const registerDomain =
      this.getConfigurationKeyValue('registerDomain') || 'localhost';
    const appDomain = this.getConfigurationKeyValue('appDomain');

    // Solução que previne os dominios do Toro Radar e Toro no cadastro não sejam reconhecidos
    const domains = ['cadastro-', 'cadastro.', registerDomain, appDomain];

    return domains.some(domain => origin.includes(domain));
  }

  /**
   * Atualiza o título da página e matatags
   * @param title título da página
   * @param metaTags meta tags
   */
  public updatePageTitle(title: string) {
    if (title) {
      this.titleService.setTitle(title);
    } else {
      this.titleService.setTitle(this.defaultTitle);
    }
  }

  /**
   * Atualiza/cria as metatags segundo lista fornecida
   * @param metaTags lista de meta tags a serem atualizads/criadas
   */
  public updateMetaTags(metaTags: Array<{ name: string; content: string }>) {
    if (metaTags && metaTags.length) {
      metaTags.forEach(tag => {
        if (this.metaService.getTags(`name=${tag.name}`).length) {
          this.metaService.updateTag(tag);
        } else {
          this.metaService.addTag(tag, true);
        }
      });
    } else {
      this.metaService.addTags(this.defaultMetaTags, true);
    }
  }

  /**
   * Define um título padrão (usado quando a rota não define o page title)
   * @param title título a ser usado
   */
  public setDefaultTitle(title: string) {
    this.defaultTitle = title ? title : this.defaultTitle;
  }

  /**
   * Define as metaTags padrões (usadas quando não são definidas na rota)
   * @param metaTags lista de meta tags
   */
  public setDefaultMetaTags(
    metaTags: Array<{ name: MetaTagType; content: string }>
  ) {
    this.defaultMetaTags =
      metaTags && metaTags.length ? metaTags : this.defaultMetaTags;
  }

  /**
   * Altera a url base de configuração
   * @param newUrl nova url
   */
  private swtichEnvironmentDomain(newUrl: string) {
    this.settings = { ...this.settings, ...{ urlBase: newUrl } };
  }

  private shouldUseNewuserApi(uri: string) {
    let isNewUserApiURI = false;

    for (let i = 0; i < TORO_USER_API_URLS.length; i += 1) {
      if (uri.includes(TORO_USER_API_URLS[i])) {
        isNewUserApiURI = true;
        break;
      }
    }

    return isNewUserApiURI;
  }
}
